import { useState } from "react";
import { saveBlob } from "../api/export";
import * as ml from "../api/ml";
import { csvRowCount, xlsxRowCount } from "../lib/training";
import { Dialog } from "../ui/Dialog";
import type { DialogApi } from "../ui/dialogs";

const plural = (n: number, word: string) => `${n} ${word}${n !== 1 ? "s" : ""}`;

/** Tools ▸ Reset Review State… (main_window._reset_review_state). Resolves true if anything changed. */
export async function resetReviewState(pid: number, dlg: DialogApi, say: (text: string) => void): Promise<boolean> {
  const title = "Reset review state";
  let reviewed: number;
  try { reviewed = (await ml.annotationStats(pid)).reviewed_annotation_count; }
  catch (e) { await dlg.alert({ title, text: `Could not read project: ${(e as Error).message}` }); return false; }
  if (!reviewed) { await dlg.alert({ title, text: "Nothing is marked reviewed in this project already." }); return false; }
  const ok = await dlg.confirm({
    title, yes: "Reset", no: "Cancel",
    text: `Clear the reviewed flag on all ${plural(reviewed, "reviewed annotation")} in this project?\n\n` +
      "No annotation is deleted — every shape, class and corner stays exactly as it is. Only the 'reviewed' mark is cleared, so training will use nothing until you review it again.\n\n" +
      "Do this when annotations were marked reviewed without anyone checking them — an imported pre-label file, for instance — and you want training to use only what you have verified yourself.",
  });
  if (!ok) return false;
  try {
    const r = await ml.unreviewAnnotations(pid);
    say(`Review state cleared on ${plural(r.cleared ?? 0, "annotation")}. Mark each one reviewed as you check it.`);
    return true;
  } catch (e) { await dlg.alert({ title: "Could not reset the review state", text: (e as Error).message }); return false; }
}

/** Tools ▸ Clear Unreviewed Annotations… (main_window._clear_unreviewed_annotations). */
export async function clearUnreviewedAnnotations(pid: number, dlg: DialogApi, say: (text: string) => void): Promise<boolean> {
  const title = "Clear unreviewed annotations";
  let unreviewed: number;
  try { unreviewed = (await ml.annotationStats(pid)).unreviewed_annotation_count; }
  catch (e) { await dlg.alert({ title, text: `Could not read project: ${(e as Error).message}` }); return false; }
  if (!unreviewed) { await dlg.alert({ title, text: "Nothing unreviewed in this project already." }); return false; }
  const ok = await dlg.confirm({
    title, danger: true, yes: "Delete them", no: "Cancel",
    text: `Permanently delete all ${plural(unreviewed, "unreviewed annotation")} in this project?\n\n` +
      "This cannot be undone. A reviewed annotation is never touched, even on an image that also carries unreviewed ones — only the unreviewed shapes are removed. An image left with nothing on it is “Not annotated” again, so auto-annotating picks it up.\n\n" +
      "Do this when you want to re-run auto-annotation — e.g. after training a new model version — on images an older model already (unreviewed-ly) annotated.",
  });
  if (!ok) return false;
  try {
    const r = await ml.clearUnreviewed(pid);
    say(`Deleted ${plural(r.annotations_deleted ?? 0, "unreviewed annotation")} across ${plural(r.images_affected ?? 0, "image")}`);
    return true;
  } catch (e) { await dlg.alert({ title: "Could not clear the unreviewed annotations", text: (e as Error).message }); return false; }
}

/** Tools ▸ Export Measurements to CSV… / to Excel (with photos)…: the options dialog, then the download. */
export function MeasurementsDialog({ projectId, xlsx, dlg, say, onClose }: { projectId: number; xlsx: boolean; dlg: DialogApi; say: (text: string) => void; onClose: () => void }) {
  const [scale, setScale] = useState(0.20445);
  const [unreviewed, setUnreviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const title = xlsx ? "Export measurements to Excel" : "Export measurements";

  const run = async () => {
    setBusy(true);
    const opts = { scale, includeUnreviewed: unreviewed };
    try {
      const blob = await (xlsx ? ml.exportMeasurementsXlsx : ml.exportMeasurementsCsv)(projectId, opts);
      const rows = xlsx ? await xlsxRowCount(blob) : csvRowCount(await blob.text());
      if (rows <= 0) {
        onClose();
        await dlg.alert({
          title,
          text: unreviewed ? "No four-corner annotations found in this project — nothing to export."
            : "No reviewed four-corner annotations found in this project. If you are checking a model's predictions, tick “Include unreviewed auto-annotations” — auto-annotate writes its output unreviewed.",
        });
        return;
      }
      const name = xlsx ? "measurements.xlsx" : "measurements.csv";
      saveBlob(blob, name);
      onClose();
      say(xlsx ? `${plural(rows, "measurement")} with photos downloaded as ${name}` : `${plural(rows, "measurement")} downloaded as ${name}`);
    } catch (e) {
      onClose();
      await dlg.alert({ title: "Could not export the measurements", text: (e as Error).message });
    }
  };

  return (
    <Dialog title={xlsx ? "Export measurements to Excel (with photos)" : "Export measurements"} onClose={onClose} width={440} testId="measure-export">
      {xlsx && (
        <p style={{ marginTop: 0, whiteSpace: "pre-line" }}>
          Click OK to download a spreadsheet with one row per package: its measurements plus a photo of it, side by side.{"\n\n"}
          • ToF captures — length, breadth and height, computed directly from their point cloud.{"\n"}
          • Photographs — length and breadth, computed from the scale you set below.
        </p>
      )}
      <div style={{ marginBottom: 6, whiteSpace: "pre-line" }}>
        {xlsx ? "Scale (mm per pixel), for photographs — an orthorectified image's constant, the same one the Measure panel uses:"
          : "Scale (mm per pixel) — for an orthorectified image, this is the same constant the Measure panel uses:"}
      </div>
      <input className="input" type="number" step="0.00001" min={0.000001} max={1000} value={scale} data-testid="mx-scale"
        onChange={(e) => setScale(Math.min(1000, Math.max(0.000001, Number(e.target.value))))} />
      <label className="check" style={{ marginTop: 8 }}
        title={`Adds the model's own predictions, which are written unreviewed. Use this to score a checkpoint against ground truth. The ${xlsx ? "file" : "CSV"} names each row's source, so predictions stay distinguishable from shapes a person drew, and nothing is marked reviewed.`}>
        <input type="checkbox" checked={unreviewed} onChange={(e) => setUnreviewed(e.target.checked)} data-testid="mx-unreviewed" />Include unreviewed auto-annotations</label>
      <div className="dialog-actions">
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn primary" disabled={busy} onClick={run} data-testid="mx-go">OK</button>
      </div>
    </Dialog>
  );
}
